import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { PMDataTable } from "../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../utils/PMTableView/CellLink";
import { TWclassNames } from "../../utils/Div";
import { caseStatusOptions } from "./utils";
import { priorityOptions } from "../quality/deviations/utils";
import {
  InitialsTag,
  StringLimit,
  dateToString,
  searchFieldsByName,
} from "../../Utils";
import { UnreadEmailTagBadge } from "./UnreadEmailTagBadge";
import {
  caseHasUnreadEmailTag,
  isUnreadEmailTag,
  unreadEmailTagLabel,
  visibleCaseTagDefinitions,
} from "./unreadInboundMail";

/**
 * Shared list fields requested from the API for the table view.
 * Avoids the heavy `items` payload — the drawer loads the timeline on demand.
 */
export const CASES_TABLE_FIELDS =
  "id,case_number,title,status,due_date,case_type,user_assigned,customer,created_at,updated_at,priority,last_item,fields,tag_definitions";

const caseCustomerName = (item) =>
  item?.customer?.id
    ? item?.customer?.displayTitle
    : item?.fields
    ? searchFieldsByName(item, "name")?.value
    : null;

const caseAddress = (item) => {
  if (
    item?.customer?.postal_address &&
    item?.customer?.postal_address?.address !== "null"
  ) {
    return item?.customer?.postal_address?.displayTitle;
  }
  if (item?.fields) {
    const address = searchFieldsByName(item, "address")?.value;
    if (address) {
      const address2 = searchFieldsByName(item, "address2")?.value;
      const postal = searchFieldsByName(item, "postal")?.value;
      const place = searchFieldsByName(item, "place")?.value;
      return `${address}${address2 ? ", " + address2 : ""}, ${postal || ""} ${place || ""}`.trim();
    }
  }
  return null;
};

const statusLabel = (value) => {
  if (!value) return null;
  const match = caseStatusOptions.find(
    (o) => String(o.value).toLowerCase() === String(value).toLowerCase()
  );
  return match ? match.label : value;
};

const priorityValue = (priority) => {
  if (priority == null || priority === "") return "";
  if (typeof priority === "object") {
    return String(priority?.id ?? priority?.value ?? "");
  }
  return String(priority);
};

const priorityLabel = (value) => {
  if (value == null || value === "") return null;
  const option = priorityOptions[Number(value) - 1];
  return option ? option.label : String(value);
};

// ──────────────────────────────────────────────
// CaseInlineSelect — robust inline dropdown for editing a single field.
//
// Two render modes:
//   • default (table): the menu is portaled to <body> and positioned `fixed`
//     near the trigger, so the table's `overflow` container never clips it.
//   • inline (drawer): the menu renders in-flow directly under the trigger.
//     This keeps it *inside* the Headless UI Dialog panel, so selecting an
//     option is NOT treated as an outside-click (which would close the drawer).
//
// options: [{ value, label, class? }]
// ──────────────────────────────────────────────
export const CaseInlineSelect = ({
  options = [],
  value,
  placeholder,
  onChange,
  inline = false,
  renderValue,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 208 });

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 208);
    let left = r.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setPos({ top: r.bottom + 4, left, width });
  };

  useLayoutEffect(() => {
    if (open && !inline) place();
  }, [open, inline]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      if (!inline) place();
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, inline]);

  const current = options.find((o) => String(o.value) === String(value ?? ""));

  const Pill = ({ option }) =>
    renderValue ? (
      renderValue(option)
    ) : (
      <span
        className={TWclassNames(
          option.class || "bg-slate-100 text-slate-700",
          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
        )}
      >
        {t(option.label)}
      </span>
    );

  const handleSelect = (e, val) => {
    e.stopPropagation();
    setOpen(false);
    onChange?.(val);
  };

  const optionButtons = options.map((opt) => {
    const isSel = String(opt.value) === String(value ?? "");
    return (
      <button
        key={opt.value}
        type="button"
        onClick={(e) => handleSelect(e, opt.value)}
        className={TWclassNames(
          "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50",
          isSel && "bg-slate-50"
        )}
      >
        <Pill option={opt} />
        {isSel && <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />}
      </button>
    );
  });

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setOpen((o) => !o);
      }}
      className="inline-flex max-w-full items-center gap-1 rounded-lg px-1.5 py-1 transition-colors hover:bg-slate-100"
    >
      {current ? (
        <Pill option={current} />
      ) : (
        <span className="text-[13px] text-slate-400">{placeholder || "—"}</span>
      )}
      <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
    </button>
  );

  if (inline) {
    return (
      <div className="relative inline-block">
        {trigger}
        {open && (
          <>
            {/* in-flow click-catcher keeps clicks inside the dialog panel */}
            <div
              className="fixed inset-0 z-[40]"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
            <div className="absolute left-0 z-[50] mt-1 max-h-64 w-56 overflow-auto rounded-xl bg-white py-1 shadow-lg ring-1 ring-slate-200">
              {optionButtons}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {trigger}
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
            <div
              className="fixed z-[9999] max-h-64 overflow-auto rounded-xl bg-white py-1 shadow-lg ring-1 ring-slate-200"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              {optionButtons}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/** Build the assignee option list (with a clear "unassigned" entry). */
export const buildAssigneeOptions = (t, userOptions = []) => [
  { value: "", label: t("Ikke tildelt"), class: "bg-slate-100 text-slate-500" },
  ...userOptions.map((u) => ({
    value: String(u.value),
    label: u.label,
    class: "bg-blue-50 text-blue-700",
  })),
];

// ──────────────────────────────────────────────
// EditableDateCell — click-to-edit native date input.
// ──────────────────────────────────────────────
const EditableDateCell = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const dateOnly = value ? String(value).split("T")[0] : "";

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
        <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
        <input
          type="date"
          defaultValue={dateOnly}
          autoFocus
          onBlur={(e) => {
            setEditing(false);
            onChange?.(e.target.value || null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setEditing(false);
              onChange?.(e.target.value || null);
            }
            if (e.key === "Escape") setEditing(false);
          }}
          className="border-0 bg-transparent p-0 text-[13px] text-slate-700 focus:outline-none focus:ring-0"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
    >
      <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
      {value ? dateToString(value) : "—"}
    </button>
  );
};

/**
 * Full column definitions (with editable cell renderers + grouping).
 *
 * @param {function} t  — i18n translator
 * @param {{ onUpdate?: function, userOptions?: Array }} [opts]
 *        onUpdate(caseItem, patch) persists an inline change.
 */
export const getCaseColumns = (t, opts = {}) => {
  const onUpdate = opts.onUpdate || (() => {});
  const assigneeOptions = buildAssigneeOptions(t, opts.userOptions || []);

  return [
    {
      key: "case_number",
      label: t("Number"),
      sortKey: "case_number",
      width: 160,
      always: true,
      cardRole: "title",
      cell: (item) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <CellLink
            to={item?.id ? `/dashboard/cases/${item.id}` : null}
            className="text-[13.5px] font-semibold tabular-nums text-blue-600"
          >
            {item.case_number}
          </CellLink>
          <CellLink
            to={item?.customer?.id ? `/dashboard/customers/${item.customer.id}` : null}
            className="truncate text-[12px] text-slate-400"
          >
            {caseCustomerName(item) || "—"}
          </CellLink>
        </div>
      ),
    },
    {
      key: "title",
      label: t("Title"),
      sortKey: "title",
      width: 260,
      cardRole: "title",
      cell: (item) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[13.5px] font-medium text-slate-800">
            {item.title ? (
              <StringLimit text={item.title} limit={60} />
            ) : (
              <span className="italic text-slate-400">{t("Mangler tittel")}</span>
            )}
          </span>
          {caseHasUnreadEmailTag(item) ? (
            <UnreadEmailTagBadge
              tag={(item.tag_definitions || []).find(isUnreadEmailTag)}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: "case_type",
      label: t("Case Type"),
      width: 150,
      groupable: true,
      cardRole: "meta",
      groupValue: (item) => item?.case_type?.title || "",
      groupLabel: (key) => key || t("table_view.no_value"),
      cell: (item) => (
        <span className="text-[13px] text-slate-500">{item?.case_type?.title || "—"}</span>
      ),
    },
    {
      key: "status",
      label: t("Status"),
      sortKey: "status",
      width: 150,
      stopRowClick: true,
      cardRole: "status",
      groupable: true,
      groupValue: (item) => item?.status || "",
      groupLabel: (key) => statusLabel(key) || t("table_view.no_value"),
      cell: (item) => (
        <CaseInlineSelect
          options={caseStatusOptions}
          value={item?.status}
          placeholder={t("Status")}
          onChange={(val) => onUpdate(item, { status: val })}
        />
      ),
    },
    {
      key: "priority",
      label: t("Priority"),
      sortKey: "priority",
      width: 150,
      stopRowClick: true,
      cardRole: "status",
      groupable: true,
      groupValue: (item) => priorityValue(item?.priority),
      groupLabel: (key) => priorityLabel(key) || t("table_view.no_value"),
      cell: (item) => (
        <CaseInlineSelect
          options={priorityOptions}
          value={priorityValue(item?.priority) || "1"}
          placeholder={t("Priority")}
          onChange={(val) =>
            onUpdate(item, { priority: val ? parseInt(val, 10) : 1 })
          }
        />
      ),
    },
    {
      key: "user_assigned",
      label: t("Assigned"),
      width: 150,
      stopRowClick: true,
      cardRole: "meta",
      groupable: true,
      groupValue: (item) => item?.user_assigned?.name || "",
      groupLabel: (key) => key || t("Ikke tildelt"),
      cell: (item) => (
        <CaseInlineSelect
          options={assigneeOptions}
          value={item?.user_assigned?.id ? String(item.user_assigned.id) : ""}
          placeholder={t("Ikke tildelt")}
          renderValue={(opt) =>
            opt.value ? (
              <InitialsTag name={opt.label} />
            ) : (
              <span className="text-[13px] text-slate-400">{opt.label}</span>
            )
          }
          onChange={(val) =>
            onUpdate(item, { user_assigned_id: val ? Number(val) : null })
          }
        />
      ),
    },
    {
      key: "due_date",
      label: t("Due date"),
      sortKey: "due_date",
      width: 150,
      stopRowClick: true,
      cardRole: "meta",
      cell: (item) => (
        <EditableDateCell
          value={item?.due_date}
          onChange={(val) => onUpdate(item, { due_date: val })}
        />
      ),
    },
    {
      key: "created_at",
      label: t("Created"),
      sortKey: "created_at",
      width: 120,
      cell: (item) => (
        <span className="text-[13px] tabular-nums text-slate-500">
          {item.created_at ? dateToString(item.created_at) : "—"}
        </span>
      ),
    },
    {
      key: "tags",
      label: t("Labels"),
      width: 180,
      cardRole: "meta",
      cell: (item) => {
        const tags = visibleCaseTagDefinitions(item);
        if (!tags.length) return <span className="text-[13px] text-slate-400">—</span>;
        return (
          <div className="flex min-w-0 flex-wrap gap-1">
            {tags.map((tag) =>
              isUnreadEmailTag(tag) ? (
                <UnreadEmailTagBadge key={tag.id} tag={tag} />
              ) : (
                <span
                  key={tag.id}
                  className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium"
                  style={
                    tag.color
                      ? { backgroundColor: tag.color + "30", color: tag.color }
                      : { backgroundColor: "#f1f5f9", color: "#334155" }
                  }
                >
                  {unreadEmailTagLabel(tag, t) || tag.name}
                </span>
              )
            )}
          </div>
        );
      },
    },
    {
      key: "updated_at",
      label: t("Updated"),
      sortKey: "updated_at",
      width: 120,
      defaultVisible: false,
      cell: (item) => (
        <span className="text-[13px] tabular-nums text-slate-500">
          {item.updated_at ? dateToString(item.updated_at) : "—"}
        </span>
      ),
    },
    {
      key: "address",
      label: t("Address"),
      width: 200,
      defaultVisible: false,
      cell: (item) => (
        <span className="block max-w-[200px] truncate text-[13px] text-slate-500">
          {caseAddress(item) || "—"}
        </span>
      ),
    },
  ];
};

export const CasesTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  layout = "table",
  hideSelection = false,
}) => {
  const { data = [], isLoading } = itemState;
  return (
    <PMDataTable
      columns={columns}
      visibleKeys={visibleKeys}
      widths={widths}
      onResize={onResize}
      groupBy={groupBy}
      sort={sort}
      onSort={onSort}
      data={data}
      isLoading={isLoading}
      density={density}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSelectionChange={onItemsSelect}
      onRowClick={onRowClick}
      layout={layout}
      hideSelection={hideSelection}
    />
  );
};

CasesTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};

export default CasesTable;
